import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight, GripVertical, ImagePlus, Loader2, Pencil, Plus, Trash2, X } from "lucide-react";
import { resolveSrc } from "@/components/Gallery";
import DiscountsAdmin from "@/pages/admin/DiscountsAdmin";
import { toast } from "sonner";
import { euro } from "@/cart";

const API = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { name: "", description: "", price: "", sizes: "", image: "", active: true };
const inputCls = "h-11 w-full border border-neutral-800 bg-black/40 px-3 text-sm text-neutral-100 placeholder:text-neutral-600 focus:border-red-700 focus:outline-none";

function ProductForm({ initial, onSave, onCancel, headers }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const uploadImage = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast("Seleziona un file immagine");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("alt", form.name || "Prodotto");
    fd.append("collection", "products");
    setUploading(true);
    try {
      const { data } = await axios.post(`${API}/api/admin/gallery`, fd, { headers: headers() });
      setForm((f) => ({ ...f, image: data.src }));
      toast("Foto caricata");
    } catch (err) {
      toast(err.response?.data?.detail || "Caricamento non riuscito");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    const cents = Math.round(parseFloat(String(form.price).replace(",", ".")) * 100);
    if (!form.name.trim() || !Number.isFinite(cents) || cents < 100) {
      toast("Inserisci nome e prezzo (min 1 €)");
      return;
    }
    setSaving(true);
    try {
      await onSave({ name: form.name.trim(), description: form.description.trim(), price_cents: cents, sizes: form.sizes.split(",").map((s) => s.trim()).filter(Boolean), image: form.image.trim(), active: form.active });
    } finally {
      setSaving(false);
    }
  };
  return (
    <form data-testid="product-form" onSubmit={submit} className="border border-red-900/40 bg-[#0D0D0D] p-5">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <input data-testid="product-name-input" value={form.name} onChange={set("name")} placeholder="Nome prodotto *" className={inputCls} />
        <input data-testid="product-price-input" value={form.price} onChange={set("price")} placeholder="Prezzo in € (es. 20)" inputMode="decimal" className={inputCls} />
        <input data-testid="product-sizes-input" value={form.sizes} onChange={set("sizes")} placeholder="Taglie separate da virgola (vuoto = nessuna)" className={inputCls} />
        <div className="flex items-center gap-3">
          {form.image ? <img src={resolveSrc(form.image)} alt="" className="h-11 w-11 shrink-0 border border-neutral-800 object-cover" /> : <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-dashed border-neutral-700 text-neutral-600"><ImagePlus className="h-4 w-4" /></span>}
          <button type="button" data-testid="product-image-upload-button" onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex h-11 flex-1 items-center justify-center gap-2 border border-neutral-700 px-3 font-mono-tac text-[10px] uppercase tracking-[0.2em] text-neutral-200 hover:border-red-600 hover:text-red-500 disabled:opacity-60">
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}
            {form.image ? "Cambia foto" : "Carica foto prodotto"}
          </button>
          <input ref={fileRef} data-testid="product-image-file-input" type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} />
        </div>
        <textarea data-testid="product-description-input" value={form.description} onChange={set("description")} rows={2} placeholder="Descrizione" className={`${inputCls} h-auto py-2 sm:col-span-2`} />
        <label className="flex items-center gap-2 text-sm text-neutral-300">
          <input data-testid="product-active-input" type="checkbox" checked={form.active} onChange={set("active")} className="accent-[#C8102E]" /> In vendita
        </label>
      </div>
      <div className="mt-4 flex gap-3">
        <button data-testid="product-save-button" type="submit" disabled={saving} className="bg-[#C8102E] px-5 py-2.5 font-mono-tac text-xs uppercase tracking-[0.2em] text-white hover:bg-[#FF2A4B] disabled:opacity-60">{saving ? "Salvo…" : "Salva prodotto"}</button>
        <button type="button" onClick={onCancel} className="inline-flex items-center gap-2 border border-neutral-800 px-4 py-2.5 font-mono-tac text-xs uppercase tracking-[0.2em] text-neutral-400 hover:text-red-500"><X className="h-3.5 w-3.5" /> Annulla</button>
      </div>
    </form>
  );
}

export default function ShopAdmin({ headers, onUnauthorized }) {
  const [products, setProducts] = useState(null);
  const [shipping, setShipping] = useState("");
  const [editing, setEditing] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data }, cfg] = await Promise.all([axios.get(`${API}/api/admin/products`, { headers: headers() }), axios.get(`${API}/api/shop/config`)]);
      setProducts(data);
      setShipping((cfg.data.shipping_cents / 100).toFixed(2));
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
      else toast("Impossibile caricare i prodotti");
    }
  }, [headers, onUnauthorized]);
  useEffect(() => {
    load();
  }, [load]);

  const handle = async (fn, ok) => {
    try {
      await fn();
      toast(ok);
      setEditing(null);
      load();
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
      else toast(err.response?.data?.detail || "Operazione non riuscita");
    }
  };

  const save = (body) =>
    editing === "new"
      ? handle(() => axios.post(`${API}/api/admin/products`, body, { headers: headers() }), "Prodotto creato")
      : handle(() => axios.put(`${API}/api/admin/products/${editing.id}`, body, { headers: headers() }), "Prodotto aggiornato");

  const saveShipping = () => {
    const cents = Math.round(parseFloat(shipping.replace(",", ".")) * 100);
    if (!Number.isFinite(cents) || cents < 0) return toast("Importo non valido");
    handle(() => axios.put(`${API}/api/admin/shop/shipping`, { shipping_cents: cents }, { headers: headers() }), "Spedizione aggiornata");
  };

  const persistOrder = async (list) => {
    setSavingOrder(true);
    try {
      await axios.put(`${API}/api/admin/products/reorder`, { ids: list.map((p) => p.id) }, { headers: headers() });
      toast("Ordine salvato");
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
      else toast(err.response?.data?.detail || "Riordino non riuscito");
      load();
    } finally {
      setSavingOrder(false);
    }
  };

  const move = (fromIdx, toIdx) => {
    if (fromIdx === toIdx || toIdx < 0 || toIdx >= products.length) return;
    const list = [...products];
    const [item] = list.splice(fromIdx, 1);
    list.splice(toIdx, 0, item);
    setProducts(list);
    persistOrder(list);
  };

  const onDrop = (targetId) => {
    if (dragId && dragId !== targetId) move(products.findIndex((p) => p.id === dragId), products.findIndex((p) => p.id === targetId));
    setDragId(null);
    setOverId(null);
  };

  return (
    <div>
      <div className="flex flex-col gap-4 border border-neutral-800 bg-[#0D0D0D] p-4 sm:flex-row sm:items-center sm:justify-between">
        <label className="flex items-center gap-3 text-sm text-neutral-300">
          Spedizione Italia (€)
          <input data-testid="shipping-input" value={shipping} onChange={(e) => setShipping(e.target.value)} inputMode="decimal" className={`${inputCls} w-28`} />
          <button data-testid="shipping-save-button" onClick={saveShipping} className="border border-neutral-700 px-3 py-2 font-mono-tac text-[10px] uppercase tracking-[0.2em] text-neutral-200 hover:border-red-600 hover:text-red-500">Salva</button>
        </label>
        {editing === null && (
          <button data-testid="product-add-button" onClick={() => setEditing("new")} className="inline-flex items-center gap-2 bg-[#C8102E] px-4 py-2.5 font-mono-tac text-xs uppercase tracking-[0.2em] text-white hover:bg-[#FF2A4B]"><Plus className="h-4 w-4" /> Nuovo prodotto</button>
        )}
      </div>
      {editing !== null && (
        <div className="mt-4">
          <ProductForm headers={headers} initial={editing === "new" ? EMPTY : { name: editing.name, description: editing.description, price: (editing.price_cents / 100).toFixed(2), sizes: editing.sizes.join(", "), image: editing.image, active: editing.active }} onSave={save} onCancel={() => setEditing(null)} />
        </div>
      )}
      <p className="mt-6 flex items-center gap-2 font-mono-tac text-[10px] uppercase tracking-[0.25em] text-neutral-500">
        <GripVertical className="h-3.5 w-3.5" /> Trascina le schede per decidere l'ordine in negozio (o usa le frecce){savingOrder && " · salvataggio…"}
      </p>
      <div data-testid="admin-products-grid" className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {products?.map((p, idx) => (
          <article
            key={p.id}
            data-testid="admin-product-card"
            draggable
            onDragStart={(e) => {
              e.dataTransfer.effectAllowed = "move";
              e.dataTransfer.setData("text/plain", p.id);
              setDragId(p.id);
            }}
            onDragEnd={() => {
              setDragId(null);
              setOverId(null);
            }}
            onDragOver={(e) => {
              if (!dragId) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = "move";
              if (overId !== p.id) setOverId(p.id);
            }}
            onDrop={(e) => {
              e.preventDefault();
              onDrop(p.id);
            }}
            className={`relative cursor-grab border bg-[#0D0D0D] transition-shadow duration-200 active:cursor-grabbing ${p.active ? "border-neutral-800" : "border-neutral-900 opacity-50"} ${
              overId === p.id && dragId !== p.id ? "border-[#C8102E] shadow-[0_0_20px_rgba(200,16,46,0.4)]" : ""
            } ${dragId === p.id ? "opacity-40" : ""}`}
          >
            <span data-testid="admin-product-order" className="absolute left-2 top-2 z-10 flex h-6 min-w-[1.5rem] items-center justify-center bg-black/80 px-1.5 font-mono-tac text-[10px] text-neutral-200">{idx + 1}</span>
            {p.image && <img src={resolveSrc(p.image)} alt="" draggable={false} className="aspect-square w-full border-b border-neutral-800 object-cover" />}
            <div className="p-4">
              <div className="flex items-start justify-between gap-2">
                <p className="font-serif-logo text-sm font-bold text-neutral-100">{p.name}</p>
                <span className="font-mono-tac text-sm text-red-500">{euro(p.price_cents)}</span>
              </div>
              <p className="font-mono-tac mt-1 text-[10px] uppercase tracking-[0.2em] text-neutral-500">{p.sizes.length ? p.sizes.join(" · ") : "Taglia unica"}{!p.active && " · nascosto"}</p>
              <div className="mt-3 flex gap-2">
                <button data-testid="admin-product-edit" onClick={() => setEditing(p)} aria-label={`Modifica ${p.name}`} className="flex h-8 w-8 items-center justify-center border border-neutral-800 text-neutral-400 hover:border-red-700 hover:text-red-500"><Pencil className="h-3.5 w-3.5" /></button>
                {p.active && (
                  <button data-testid="admin-product-delete" onClick={() => window.confirm(`Togliere "${p.name}" dalla vendita?`) && handle(() => axios.delete(`${API}/api/admin/products/${p.id}`, { headers: headers() }), "Prodotto nascosto")} aria-label={`Nascondi ${p.name}`} className="flex h-8 w-8 items-center justify-center border border-neutral-800 text-neutral-600 hover:border-red-700 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                )}
                <div className="ml-auto flex gap-1">
                  <button data-testid="admin-product-move-left" onClick={() => move(idx, idx - 1)} disabled={idx === 0} aria-label="Sposta prima" className="flex h-8 w-8 items-center justify-center border border-neutral-800 text-neutral-400 hover:border-red-700 hover:text-red-500 disabled:opacity-30"><ChevronLeft className="h-3.5 w-3.5" /></button>
                  <button data-testid="admin-product-move-right" onClick={() => move(idx, idx + 1)} disabled={idx === products.length - 1} aria-label="Sposta dopo" className="flex h-8 w-8 items-center justify-center border border-neutral-800 text-neutral-400 hover:border-red-700 hover:text-red-500 disabled:opacity-30"><ChevronRight className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            </div>
          </article>
        ))}
      </div>
      <DiscountsAdmin headers={headers} onUnauthorized={onUnauthorized} />
    </div>
  );
}
